import type { BoardTaskRow, TaskBoardProjection, TaskState } from './projections.ts';

/** The three read-only Task-board arrangements. Exactly one is selected by the surface. */
export type TaskBoardGrouping = 'state' | 'owner' | 'metric';

/** One rendered Task-board column. `key` is React-only and must never be rendered as display copy. */
export interface TaskBoardGroup {
  readonly key: string;
  readonly label: string;
  readonly count: number;
  readonly cards: readonly BoardTaskRow[];
  readonly lifecycleState: string | null;
}

// The canonical lifecycle order only orders state keys that the projection actually carries. Unknown
// projection keys remain visible at the end rather than being silently discarded.
const STATE_ORDER: readonly TaskState[] = [
  'draft',
  'briefed',
  'working',
  'blocked',
  'done',
  'dropped',
];

function compareText(left: string, right: string): number {
  const leftFolded = left.toLowerCase();
  const rightFolded = right.toLowerCase();
  if (leftFolded < rightFolded) return -1;
  if (leftFolded > rightFolded) return 1;
  if (left < right) return -1;
  if (left > right) return 1;
  return 0;
}

function orderedStates(counts: Readonly<Record<string, number>>): string[] {
  const keys = Object.keys(counts);
  const known = STATE_ORDER.filter((state) => keys.includes(state));
  const extra = keys.filter((key) => !STATE_ORDER.includes(key as TaskState)).sort(compareText);
  return [...known, ...extra];
}

function nonBlank(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function groupByState(board: TaskBoardProjection): readonly TaskBoardGroup[] {
  const cardsByState = new Map<string, BoardTaskRow[]>();
  for (const card of board.filtered) {
    const cards = cardsByState.get(card.state) ?? [];
    cards.push(card);
    cardsByState.set(card.state, cards);
  }
  return orderedStates(board.countsByState).map((state) => ({
    key: `state:${state}`,
    label: state,
    count: board.countsByState[state as TaskState] ?? 0,
    cards: cardsByState.get(state) ?? [],
    lifecycleState: state,
  }));
}

interface MutableGroup {
  readonly key: string;
  readonly label: string;
  readonly cards: BoardTaskRow[];
}

function addCard(
  groups: Map<string, MutableGroup>,
  descriptor: Pick<MutableGroup, 'key' | 'label'>,
  card: BoardTaskRow,
): void {
  const group = groups.get(descriptor.key) ?? { ...descriptor, cards: [] };
  group.cards.push(card);
  groups.set(descriptor.key, group);
}

function ownerGroup(card: BoardTaskRow): Pick<MutableGroup, 'key' | 'label'> {
  const ownerId = nonBlank(card.ownerUserId);
  if (ownerId === null) return { key: 'owner-slot:missing', label: 'Unassigned' };
  return {
    key: `owner-id:${ownerId}`,
    label: nonBlank(card.ownerDisplayName) ?? 'Owner unavailable',
  };
}

const NON_METRIC_LABELS = {
  hygiene: 'Hygiene',
  compliance: 'Compliance',
  risk_control: 'Risk control',
  general: 'General',
} as const;

function nonMetricGroup(card: BoardTaskRow): Pick<MutableGroup, 'key' | 'label'> {
  const category = nonBlank(card.nonMetricCategory);
  if (category !== null && Object.hasOwn(NON_METRIC_LABELS, category)) {
    const knownCategory = category as keyof typeof NON_METRIC_LABELS;
    return {
      key: `non-metric:${knownCategory}`,
      label: NON_METRIC_LABELS[knownCategory],
    };
  }
  return {
    key: category === null ? 'metric-link:malformed' : `metric-link:malformed:${category}`,
    label: 'KPI link unavailable',
  };
}

function metricGroup(card: BoardTaskRow): Pick<MutableGroup, 'key' | 'label'> {
  if (card.metricId === null) return nonMetricGroup(card);
  const metricId = nonBlank(card.metricId);
  if (metricId === null) {
    return { key: 'metric-link:malformed-id', label: 'KPI link unavailable' };
  }
  return {
    key: `metric-id:${metricId}`,
    label: nonBlank(card.metricName) ?? 'KPI link unavailable',
  };
}

function groupByResolvedDimension(
  cards: readonly BoardTaskRow[],
  descriptorFor: (card: BoardTaskRow) => Pick<MutableGroup, 'key' | 'label'>,
): readonly TaskBoardGroup[] {
  const groups = new Map<string, MutableGroup>();
  for (const card of cards) addCard(groups, descriptorFor(card), card);
  return [...groups.values()]
    .sort((left, right) => compareText(left.label, right.label) || compareText(left.key, right.key))
    .map((group) => ({
      key: group.key,
      label: group.label,
      count: group.cards.length,
      cards: group.cards,
      lifecycleState: null,
    }));
}

/** Single source of truth for Task-board grouping, safe labels, counts and deterministic column order. */
export function groupTaskBoard(
  board: TaskBoardProjection,
  grouping: TaskBoardGrouping,
): readonly TaskBoardGroup[] {
  switch (grouping) {
    case 'state':
      return groupByState(board);
    case 'owner':
      return groupByResolvedDimension(board.filtered, ownerGroup);
    case 'metric':
      return groupByResolvedDimension(board.filtered, metricGroup);
  }
}
